import { type ApolloClient, type NormalizedCacheObject, type QueryResult, useSubscription } from "@apollo/client";
import { LockState, type Maybe } from "gql/graphql";
import { handleSetInitialData, useInitialStateQuery } from "hooks/device/useInitialStateQuery";
import { useDoorAndLockState } from "hooks/useDoorAndLockState";
import { useFetchUnknownDishTypes } from "hooks/useFetchUnknownDishTypes";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useQrCodeLifecycleLogging } from "hooks/useQrCodeLifecycleLogging";
import { useRefetchDishTypesQuery } from "hooks/useRefetchDishTypesQuery";
import { useAppDispatch, useAppSelector, useAppStore } from "hooks/utilsHooks/storeHooks";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useIdleTimerContext } from "react-idle-timer";
import { Config } from "services/configuration/eatSmartConfig";
import { selectFridgeErrorType, setFridgeErrorType } from "slices/fridgeInfoSlice";
import { DoorState, ErrorType, type FridgeSubscription, LightingSegmentModeType, type Subscription } from "types";
import type { LocalFridgeState } from "types/mainTypes";
import { getFridgeId, getTabletTypeFromUrl, validateFridgeId } from "utils/browser/localStorage";
import { debugTrace, shouldTraceLightingDebug } from "utils/debug/debugTrace";
import { networkTrace, shouldTraceNetworkDebug } from "utils/debug/networkTrace";
import { FRIDGE_SUBSCRIPTION } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { deepOmit } from "utils/utils";
import { applyFridgeFrame } from "./applyFridgeFrame";
import { handleSubscriptionTermination } from "./handleSubscriptionTermination";
import { resolveShouldSkipSubscription, shouldLogSkipFlip } from "./resolveShouldSkipSubscription";
import { useClearBrokenOnInbound } from "./useClearBrokenOnInbound";
import { useClearRecoveryStateOnConflict } from "./useClearRecoveryStateOnConflict";
import { useLogSubscriptionRestored } from "./useLogSubscriptionRestored";
import { useReconnectAfterBrokenSubscription } from "./useReconnectAfterBrokenSubscription";
import { useRecoverFromFridgeError } from "./useRecoverFromFridgeError";
import { useReloadOnUnrecoveredSubscription } from "./useReloadOnUnrecoveredSubscription";
import { useSubscriptionLivenessWatchdog } from "./useSubscriptionLivenessWatchdog";

export type DoorStateType = {
	state: DoorState | undefined;
	lock: {
		state: LockState | undefined;
		timeout: number | undefined | null;
	};
};

export type DeviceContextType = {
	id: string;
	loading: boolean;
	appConnected: boolean;
	handleRefetchDishTypes: () => Promise<void>;
	door: DoorStateType;
	initialQueryData: QueryResult | null;
};

type DeviceProviderProps = {
	client: ApolloClient<NormalizedCacheObject> | null;
	appConnected: boolean;
	setIsConnected: (newState: boolean) => void;
	isSubscriptionBroken: boolean;
	setIsSubscriptionBroken: (newState: boolean) => void;
};

const initialState: DeviceContextType = {
	id: "",
	loading: true,
	appConnected: true,
	handleRefetchDishTypes: () => Promise.resolve(),
	door: {
		state: DoorState.Closed,
		lock: {
			state: LockState.Locked,
			timeout: 0,
		},
	},
	initialQueryData: null,
};

export const DeviceContext = createContext<DeviceContextType>({
	...initialState,
	appConnected: true,
	loading: true,
});

export const DeviceContextProvider = ({
	children,
	appConnected,
	setIsConnected,
	isSubscriptionBroken,
	setIsSubscriptionBroken,
}: PropsWithChildren<DeviceProviderProps>) => {
	getTabletTypeFromUrl();
	const fridgeId = getFridgeId() ?? "";
	const appDispatch = useAppDispatch();
	// Used by onData for synchronous prev/next slot reads — dispatch is synchronous, so
	// store.getState() is never stale, unlike a closed-over selector value when rapid
	// subscription pushes arrive before React re-renders.
	const store = useAppStore();
	const idleTimer = useIdleTimerContext();
	const { doors, lock, lockTimeout } = useDoorAndLockState();
	const { trackDoorStateChange, trackRefillStarted, trackRefillFinished } = useMixpanelTrackers();
	const { getUnknownDishTypes } = useFetchUnknownDishTypes();
	const lastTrackedDoorStateRef = useRef<DoorState | null>(null);

	const {
		data,
		loading: initialQueryLoading,
		refetch: initialQueryRefetch,
	} = useInitialStateQuery({
		fridgeId: fridgeId,
	});
	const { loading: refetchDishTypesLoading, handleRefetchDishTypes } = useRefetchDishTypesQuery({
		fridgeId: fridgeId,
	});
	// Records the QR a transaction started with vs. the one shown once it ended, AND self-heals: at the
	// finished edge it refetches a fresh code when a (remote-refill) QR didn't rotate, so the next
	// customer never scans the previous one's code.
	useQrCodeLifecycleLogging({ fridgeId: fridgeId });

	// Skip is a latched startup gate (see resolveShouldSkipSubscription). hasStartedRef latches true
	// the first time the initial load yields a usable fridge, so a later transient refetch error can't
	// re-skip and strand a running subscription. Persistent CONFLICT/UNAUTHORIZED errors still close it.
	const isFridgeIdValid = validateFridgeId(fridgeId);
	const hasValidFridge = Boolean(data?.fridge) && !data?.fridge?.error && isFridgeIdValid;
	const hasStartedRef = useRef(false);
	if (hasValidFridge) {
		hasStartedRef.current = true;
	}
	const isUnauthorized = data?.fridge?.error?.type === ErrorType.UNAUTHORIZED;
	const isConflict = data?.fridge?.error?.type === ErrorType.CONFLICT;
	const shouldSkipSubscription = resolveShouldSkipSubscription({
		isFridgeIdValid: isFridgeIdValid,
		hasValidFridge: hasValidFridge,
		isUnauthorized: isUnauthorized,
		isConflict: isConflict,
		hasStarted: hasStartedRef.current,
	});

	useClearRecoveryStateOnConflict({
		isConflict: isConflict,
		appConnected: appConnected,
		isSubscriptionBroken: isSubscriptionBroken,
		setIsConnected: setIsConnected,
		setIsSubscriptionBroken: setIsSubscriptionBroken,
	});

	// Read by the onComplete/onError callbacks below, which would otherwise close over a stale value.
	const shouldSkipRef = useRef(shouldSkipSubscription);
	shouldSkipRef.current = shouldSkipSubscription;

	// Instrumentation (root cause #1): a runtime skip-flip false→true after startup is the signature the
	// latch is meant to make impossible. Log it (with the causing fridge.error) so the field confirms the
	// fix held — and surfaces any new path that strands a running stream.
	const prevSkipRef = useRef(shouldSkipSubscription);
	useEffect(() => {
		if (shouldLogSkipFlip(hasStartedRef.current, prevSkipRef.current, shouldSkipSubscription)) {
			const fridgeError = data?.fridge?.error;
			logToSentry(
				`Subscription skip latched true after startup (unauthorized=${isUnauthorized}, conflict=${isConflict}, validId=${isFridgeIdValid}), fridge: ${fridgeId}`,
				"warning",
				fridgeError ? [fridgeError] : [],
			);
		}
		prevSkipRef.current = shouldSkipSubscription;
	}, [shouldSkipSubscription, isUnauthorized, isConflict, isFridgeIdValid, fridgeId, data?.fridge?.error]);

	const { restart } = useSubscription<Subscription>(FRIDGE_SUBSCRIPTION, {
		// If there is an error in the initial state query, we do not connect subscription
		skip: shouldSkipSubscription,
		variables: { id: fridgeId },
		// By using onData callback, we handle situation when messages from API
		// are delivered in the same time
		onData: ({ data }) => {
			if (data?.data?.fridge?.error) {
				logToSentry(`Error is subscription, fridge: ${fridgeId}`, "error", [data.data.fridge.error]);
			}
			// TEMP network diagnostic: a frame arriving is the "subscription healthy / reconnected"
			// signal. Paired with the [ws] BROKEN / reconnect lines it shows the broken→recovered story.
			if (shouldTraceNetworkDebug()) {
				networkTrace(data?.data?.fridge?.error ? "[ws] frame error" : "[ws] frame received");
			}
			setIsSubscriptionBroken(false);
			// A live subscription frame is the most authoritative "we're connected" signal. Now that a
			// subscription error flips us offline on the FIRST error (resolveRetryAttempt), recover the
			// instant data flows again instead of waiting on the Google-DNS ping — and clear any false
			// offline left by a non-subscription query failure while the stream was actually healthy.
			setIsConnected(true);
			const cleanedData = deepOmit<Maybe<FridgeSubscription> | undefined, LocalFridgeState>(data.data?.fridge, [
				"__typename",
			]);
			// TEMP diagnostic: log what each FRIDGE_SUBSCRIPTION frame actually carries for lighting, so a
			// capture shows the BE-pushed state directly (not just the derived Redux writes). Pair with the
			// converger's FIRE-SWITCH / SWITCH-OK / FIRE-TOGGLE lines to see send vs ack vs subscription.
			if (shouldTraceLightingDebug()) {
				const dishTypes = cleanedData.dishes?.types ?? null;
				const spotlitIds = dishTypes
					? dishTypes
							.filter((dishType) => dishType?.lighting?.mode === LightingSegmentModeType.Spotlight)
							.map((dishType) => dishType?.id ?? "?")
					: null;
				// ids (not just count) so a BE-pushed spotlight set wider than the FE's FIRE-TOGGLE target names the stray segment.
				// orderPrefs = the customer allergen preference the BE put ON the order frame — empty means the BE
				// did not hand the FE the preference (so the FE cannot merge it; if dishes still light up, the BE applied it itself).
				const orderPrefs = cleanedData.transaction?.orders?.last?.user?.preferences?.allergens ?? null;
				debugTrace(
					`[state-debug] SUB lighting.mode=${cleanedData.lighting?.mode ?? "-"} dishTypes=${cleanedData.dishes?.types?.length ?? "-"} spot=${spotlitIds?.length ?? "-"} spotIds=[${spotlitIds?.join(",") ?? ""}] order=${cleanedData.transaction?.orders?.last?.state ?? "-"} orderPrefs=[${orderPrefs?.join(",") ?? ""}] refill=${cleanedData.transaction?.refills?.last?.state ?? "-"} door=${cleanedData.door?.state ?? cleanedData.door?.lock?.state ?? "-"}`,
				);
			}

			// The Redux fan-out for one frame lives in applyFridgeFrame (extracted so the sparse-delta
			// semantics — the 1.14.x lighting/refill bug seam — are unit-testable). getState is the live
			// store read, needed because dispatch is synchronous and rapid pushes can precede a re-render.
			applyFridgeFrame({
				cleanedData: cleanedData,
				dispatch: appDispatch,
				getState: store.getState,
				trackDoorStateChange: trackDoorStateChange,
				trackRefillStarted: trackRefillStarted,
				trackRefillFinished: trackRefillFinished,
				getUnknownDishTypes: getUnknownDishTypes,
				idleTimer: idleTimer,
				lastTrackedDoorStateRef: lastTrackedDoorStateRef,
				fridgeId: fridgeId,
			});
		},
		onComplete: () =>
			handleSubscriptionTermination({
				reason: "complete",
				shouldSkip: shouldSkipRef.current,
				fridgeId: fridgeId,
				setIsSubscriptionBroken: setIsSubscriptionBroken,
			}),
		onError: () =>
			handleSubscriptionTermination({
				reason: "error",
				shouldSkip: shouldSkipRef.current,
				fridgeId: fridgeId,
				setIsSubscriptionBroken: setIsSubscriptionBroken,
			}),
	});

	// restart() throws (Apollo invariant 57) if the subscription is currently skipped, so guard it.
	const restartSubscription = useCallback(() => {
		if (!shouldSkipSubscription) {
			restart();
		}
	}, [restart, shouldSkipSubscription]);

	useReconnectAfterBrokenSubscription({
		isSubscriptionBroken: isSubscriptionBroken,
		appConnected: appConnected,
		fridgeId: fridgeId,
		refetch: initialQueryRefetch,
		onData: (fridge) => handleSetInitialData(fridge, appDispatch),
		restartSubscription: restartSubscription,
	});

	// An errored boot (CONFLICT / UNAUTHORIZED / any fridge error) startup-skips the subscription and
	// nothing re-runs the initial query; when the hourly price refetch clears the error, re-pull
	// initial state so the fresh error-free data lets the startup latch open the subscription (JID-2257).
	const fridgeErrorType = useAppSelector(selectFridgeErrorType);
	useRecoverFromFridgeError({
		fridgeErrorType: fridgeErrorType,
		isSubscriptionSkipped: shouldSkipSubscription,
		fridgeId: fridgeId,
		refetch: initialQueryRefetch,
		onData: (fridge) => handleSetInitialData(fridge, appDispatch),
		// Recovery failed terminally — re-raise the error the hook retained (NOT read back from Apollo
		// data, which a failing { fridge: null } completion overwrites). The error screen returns
		// (honest: the fridge's real state is unknown) and the next healthy hourly poll re-creates the
		// recovery edge, making the hourly cadence the outer retry loop of this one-shot flight.
		onRecoveryFailed: (errorType) => appDispatch(setFridgeErrorType(errorType)),
	});

	useSubscriptionLivenessWatchdog({
		appConnected: appConnected,
		shouldSkip: shouldSkipSubscription,
		isSubscriptionBroken: isSubscriptionBroken,
		fridgeId: fridgeId,
		staleThresholdMs: Config.connectionSettings.connectionTimeout,
		setIsSubscriptionBroken: setIsSubscriptionBroken,
	});

	useClearBrokenOnInbound({
		isSubscriptionBroken: isSubscriptionBroken,
		appConnected: appConnected,
		shouldSkip: shouldSkipSubscription,
		setIsSubscriptionBroken: setIsSubscriptionBroken,
	});

	// Symmetric to the broken-subscription logs: emit one "restored" event per outage on the recovery edge.
	// Skipped so an intentional CONFLICT/UNAUTHORIZED teardown (useClearRecoveryStateOnConflict clearing the
	// broken flag) is not misreported as a genuine stream recovery.
	useLogSubscriptionRestored({
		isSubscriptionBroken: isSubscriptionBroken,
		shouldSkip: shouldSkipSubscription,
		fridgeId: fridgeId,
	});

	useReloadOnUnrecoveredSubscription({
		isSubscriptionBroken: isSubscriptionBroken,
		appConnected: appConnected,
		shouldSkip: shouldSkipSubscription,
		fridgeId: fridgeId,
	});

	const loading = initialQueryLoading || refetchDishTypesLoading;
	// Memoized so consumers of useDeviceContext() re-render only when one of these inputs
	// changes, not on every provider render.
	const contextValue = useMemo<DeviceContextType>(
		() => ({
			id: fridgeId,
			appConnected: appConnected,
			loading: loading,
			door: {
				state: doors,
				lock: {
					state: lock,
					timeout: lockTimeout,
				},
			},
			handleRefetchDishTypes: handleRefetchDishTypes,
			initialQueryData: data,
		}),
		[fridgeId, appConnected, loading, doors, lock, lockTimeout, handleRefetchDishTypes, data],
	);

	return <DeviceContext.Provider value={contextValue}>{children}</DeviceContext.Provider>;
};

export const useDeviceContext = () => useContext(DeviceContext);
